import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { ClipboardCheck, LoaderCircle } from 'lucide-react'
import { submittalVerdictVariant } from '@/lib/status-variants'

import * as Mutation from '../../../api/gql/mutation'
import FolderPicker from './FolderPicker'

const VERDICTS = ['APPROVED', 'APPROVED_AS_NOTED', 'REVISE_AND_RESUBMIT', 'REJECTED'] as const
type Verdict = (typeof VERDICTS)[number]

interface ConsolidateDialogProps {
  submittalId: string
  projectId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  refetch: () => void
  /** Same `workflowPlan.targetFolderId` snapshot PublishDialog pre-selects with
   *  — the folder the workflow template nominated at submit time. Only a
   *  pre-selection; the user can still change it before confirming. */
  defaultFolderId?: string | null
}

/**
 * Mounted only while open, same conditional-mount convention as the rest of
 * this page's dialogs — every open starts from a clean verdict/note/folder
 * selection.
 *
 * Confirm does two independent things when the verdict is a publishable one
 * and a folder was picked: consolidateSubmittal, then (only if that
 * succeeded) publishSubmittalToDocControl. They're two separate try/catch
 * blocks on purpose — see handleConfirm below — because a failure in the
 * second call must never be reported as "nothing happened" when the
 * consolidation itself already committed server-side.
 */
const ConsolidateDialog: React.FC<ConsolidateDialogProps> = ({
  submittalId,
  projectId,
  open,
  onOpenChange,
  refetch,
  defaultFolderId,
}) => {
  const { t } = useTranslation()
  const [verdict, setVerdict] = useState<Verdict | ''>('')
  const [note, setNote] = useState('')
  const [folderId, setFolderId] = useState<string | null>(defaultFolderId ?? null)
  const [submitting, setSubmitting] = useState(false)

  const [consolidateSubmittal] = useMutation(Mutation.CONSOLIDATE_SUBMITTAL)
  const [publishToDocControl] = useMutation(Mutation.PUBLISH_SUBMITTAL_TO_DOC_CONTROL)

  const canPublishNow = verdict === 'APPROVED' || verdict === 'APPROVED_AS_NOTED'

  const handleVerdictChange = (next: string) => {
    setVerdict(next as Verdict)
    // Hide-and-forget: a folder picked while APPROVED* was selected has no
    // meaning once the verdict moves away from a publishable one. Reverting to
    // the workflow's own target rather than to empty keeps a detour through a
    // non-publishable verdict from silently costing the prefill.
    if (next !== 'APPROVED' && next !== 'APPROVED_AS_NOTED') setFolderId(defaultFolderId ?? null)
  }

  const handleClose = () => {
    if (submitting) return
    onOpenChange(false)
  }

  const handleConfirm = async () => {
    if (!verdict) return
    setSubmitting(true)

    try {
      await consolidateSubmittal({ variables: { id: submittalId, verdict, note: note.trim() || null } })
    } catch (error: any) {
      toastr.error(t('Common_error'), error.message)
      setSubmitting(false)
      return
    }

    if (canPublishNow && folderId) {
      try {
        await publishToDocControl({ variables: { id: submittalId, folderId } })
        toastr.success('', t('Submittals_consolidate_success_published'))
      } catch (error: any) {
        toastr.error(t('Common_error'), t('Submittals_consolidate_publish_failed', { error: error.message }))
      }
    } else {
      toastr.success('', t('Submittals_consolidate_success'))
    }

    refetch()
    setSubmitting(false)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) handleClose() }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-primary" />
            {t('Submittals_consolidate_title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t('Submittals_consolidate_verdict_label')}</Label>
            <RadioGroup value={verdict} onValueChange={handleVerdictChange}>
              {VERDICTS.map((v) => (
                <div key={v} className="flex items-center gap-2.5 rounded px-1 py-1 hover:bg-muted">
                  <RadioGroupItem value={v} id={`consolidate-verdict-${v}`} />
                  <Label htmlFor={`consolidate-verdict-${v}`} className="flex-1 cursor-pointer font-normal">
                    <Badge variant={submittalVerdictVariant(v)} className="whitespace-nowrap font-medium">
                      {t(`Submittal_verdict_${v}`)}
                    </Badge>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="consolidate-note">{t('Submittals_consolidate_note_label')}</Label>
            <Textarea
              id="consolidate-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('Submittals_consolidate_note_placeholder')}
              rows={3}
            />
          </div>

          {canPublishNow && (
            <div className="space-y-1.5 rounded-md border border-dashed p-3">
              <Label>{t('Submittals_consolidate_publish_now_label')}</Label>
              <p className="text-xs text-muted-foreground">{t('Submittals_consolidate_publish_now_hint')}</p>
              <FolderPicker projectId={projectId} value={folderId} onChange={setFolderId} />
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t pt-4">
          <Button type="button" variant="outline" onClick={handleClose} disabled={submitting}>
            {t('Common_cancel')}
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={submitting || !verdict}>
            {submitting && <LoaderCircle className="h-4 w-4 animate-spin" />}
            {t('Submittals_consolidate_confirm_btn')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default ConsolidateDialog
